import * as React from 'react';
import { Events } from '../../types';
// tslint:disable-next-line: no-var-requires
require('./assets/map.css');

const { useEffect, useState, useRef, useImperativeHandle } = React;

export interface MapProps {
  plugins?: any[];
  mapOptions?: { [key: string]: any };
  fullScreen?: boolean;
  hideFitView?: boolean;
  style?: any;
  children?: any;
  name?: string; // 当同一页面需要获取多于一个Map实体
  events?: Events;
  instance?: any;
}

export const setAMapVersion = (version: string) => {
  (window as any).AMapVersion = version;
};

export const getAMapVerion = () => (window as any).AMapVersion;

export const setAMapKey = (key: string) => {
  (window as any).AMapKey = key;
};

export const getAMapKey = () => (window as any).AMapKey;

export function requireMap(callback: () => any) {
  // 如果不存在全局的AMap对象，则按需引入
  if (!(window as any).AMap) {
    if (!(window as any).AMapVersion || !(window as any).AMapKey) {
      throw new Error('Please use method setAMapVersion、setAMapKey, set AMap Version and Key!');
    }

    const url = `https://webapi.amap.com/maps?v=${getAMapVerion()}&key=${getAMapKey()}&plugin=AMap.Autocomplete,AMap.TruckDriving,AMap.Geocoder`;

    const jsapi = document.createElement('script');

    jsapi.addEventListener('load', (e) => {
      callback();
    });

    jsapi.charset = 'utf-8';
    jsapi.src = url;
    document.head.appendChild(jsapi);
  } else {
    callback();
  }
}

export const MapContext = React.createContext(null);


function Map(props: MapProps) {
  const {
    plugins, style, mapOptions, fullScreen, name, events, instance, hideFitView
  } = props;

  const [map, setMap]: [AMap.Map | null, (params: any) => void] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const mapContainer: { current: HTMLDivElement | null } = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // 加载地图脚本
  useEffect(() => {
    requireMap(
      () => {
        setScriptLoaded(true);
      },
    );
    return () => {
      if ((Map as any).map) {
        if (name) {
          (((Map as any).map as any)[name] as AMap.Map).destroy();
          delete (Map as any).map[name];
        } else {
          ((Map as any).map as AMap.Map).destroy();
          delete (Map as any).map;
        }
      }
    };
  }, []);

  // 初始化地图
  useEffect(() => {
    if (!scriptLoaded || !(window as any).AMap) {
      return;
    }

    // 初始化地图
    const mapEntity = new AMap.Map(mapContainer.current as HTMLDivElement, {
      center: [116.39, 39.9],
      zoom: 11,
      ...mapOptions,
    });
    if (events) {
      Object.keys(events).forEach((eventName: string) => {
        // 这里可以对事件进行特殊的处理
        mapEntity.on(eventName, (e: any) => {
          const newEvent = {
            ...e,
            map: mapEntity,
          };
          events[eventName](newEvent);
        });
      });
    }

    (window as any).superMap++;

    // 加载插件
    AMap.plugin(([
      'AMap.ToolBar',
    ].concat(plugins || [])) as any[], () => {
      // 在图面添加工具条控件，工具条控件集成了缩放、平移、定位等功能按钮在内的组合控件
      (mapEntity).addControl(new (AMap as any).ToolBar({
        // 简易缩放模式，默认为 false
        liteStyle: true,
      }));
    });

    setMap(mapEntity);

    if (name) {
      (Map as any).map = (Map as any).map || {};
      (Map as any).map[name] = mapEntity;
    } else {
      (Map as any).map = mapEntity;
    }

    // 销毁地图
    return () => {
      if (map) {
        (map as AMap.Map).destroy();
      }
    };
  }, [(window as any).AMap, scriptLoaded]);


  const containerStyle = {
    width: '100%',
    height: '100%',
    background: '#ddd',
  };
  const loadingStyle = {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
  };
  const fullMapStyle = {
    width: `${window.innerWidth}px`,
    height: `${window.innerHeight}px`,
  };

  const handleFullScreen = () => {
    isFullScreen ? setIsFullScreen(false) : setIsFullScreen(true);
  };

  const handleFitView = () => {
    if (map && (Map as any).map) {
      if (name) {
        (((Map as any).map as unknown) as any)[name].setFitView();
      } else {
        (((Map as any).map as unknown) as AMap.Map).setFitView();
      }
    }
  };

  useImperativeHandle(instance, () => ({
    handleFitView,
    // 对外暴露 map实例
    map: map,
  }));

  return (
    <div className={isFullScreen ? 'fullMapContainer mapContainer' : 'mapContainer'} style={isFullScreen ? fullMapStyle : { ...style }}>
      <MapContext.Provider value={map}>
        <div style={{ ...containerStyle, position: 'relative' }} ref={mapContainer}>
          <span style={{ ...loadingStyle, position: 'absolute' }}>Map is Loading...</span>
        </div>
        { props.children }
      </MapContext.Provider>
      {/* 地图工具条 */}
      <div className={isFullScreen ? 'toolbarContainer fullToolbarContainer' : 'toolbarContainer'}>
        {
          fullScreen
          && (
          <div
            className='fullBtn'
            onClick={() => handleFullScreen()}
          />
          )
        }
        {
          !hideFitView && (
            <div
              className='fitViewBtn'
              onClick={() => handleFitView()}
            />
          )
        }
      </div>
    </div>
  );
}

export default Map;
